import React from 'react';

const TermsOfServicePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 text-slate-900">
      {/* Custom Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
        
        .font-display { 
          font-family: 'Space Grotesk', system-ui, sans-serif; 
          letter-spacing: -0.04em;
          font-feature-settings: "liga", "kern";
        }
        .font-body { 
          font-family: 'Inter', system-ui, sans-serif; 
          font-feature-settings: "liga", "kern";
          line-height: 1.65;
        }
        
        .text-gradient-purple {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .glass-premium {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.9);
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08), 0 1px 0 rgba(255, 255, 255, 0.4) inset;
        }
      `}</style>

      {/* Navigation */}
      <nav className="fixed top-4 left-4 right-4 md:top-8 md:left-1/2 md:transform md:-translate-x-1/2 md:right-auto z-50 glass-premium backdrop-blur-xl rounded-2xl md:rounded-full px-4 md:px-8 py-3 md:py-4 shadow-2xl max-w-none md:max-w-fit">
        <div className="flex items-center justify-between md:gap-10">
          <div className="font-display text-lg md:text-xl text-gradient-purple">
          receptioneer.ai
          </div>
          <div className="hidden lg:flex items-center gap-8">
            <a href="/" className="font-body text-slate-600 hover:text-indigo-600 transition-colors font-medium">
              Home
            </a>
            <a href="/privacy" className="font-body text-slate-600 hover:text-indigo-600 transition-colors font-medium">
              Privacy
            </a>
          </div>
          <a 
            href="/"
            className="bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 md:px-6 py-2 md:py-3 rounded-full font-semibold transition-all duration-300 text-sm md:text-base hover:shadow-lg"
          >
            Back to Home
          </a>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-24 md:pt-32 pb-16 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-normal mb-6 text-slate-800 leading-tight">
              Terms of Service
            </h1>
            <div className="inline-flex items-center gap-2 glass-premium rounded-full px-6 py-3 text-slate-600 font-medium text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Effective January 1, 2025
            </div>
          </div>

          <div className="glass-premium rounded-2xl p-8 md:p-12 shadow-xl">
            <div className="prose prose-slate max-w-none font-body">
              
              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Agreement to Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  By accessing or using receptioneer.ai ("Service"), you agree to be bound by these Terms of Service ("Terms"). 
                  If you disagree with any part of these terms, you may not access the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Description of Service</h2>
                <p className="text-slate-600 mb-4">receptioneer.ai provides AI-powered receptionist services for service businesses, including but not limited to:</p>
                <ul className="space-y-2 text-slate-600">
                  <li>24/7 call answering and customer interaction</li>
                  <li>Appointment scheduling and calendar integration</li>
                  <li>Emergency call detection and routing</li>
                  <li>Lead capture and qualification</li>
                  <li>Business information management</li>
                  <li>Real-time analytics and reporting</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Account Registration and Eligibility</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Eligibility</h3>
                  <p className="text-slate-600">
                    You must be at least 18 years old and have the legal capacity to enter into contracts. 
                    By using our Service, you represent and warrant that you meet these requirements.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Account Registration</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>You must provide accurate, complete, and current information during registration</li>
                    <li>You're responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You're responsible for all activities that occur under your account</li>
                    <li>You must notify us immediately of any unauthorized use of your account</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Business Use Only</h3>
                  <p className="text-slate-600">
                    Our Service is designed for legitimate business use only. Personal or non-commercial use is not permitted under these Terms.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Subscription and Billing</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Pricing</h3>
                  <p className="text-slate-600">
                    Current pricing is $499/month for unlimited AI receptionist service. Pricing is subject to change with 30 days' written notice.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Free Trial</h3>
                  <p className="text-slate-600">
                    New customers receive a 10-day free trial. No credit card is required for the trial period. Trial accounts have full access to all features.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Billing Terms</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>Subscription fees are billed monthly in advance</li>
                    <li>Payments are due immediately upon billing</li>
                    <li>All fees are non-refundable except as expressly stated in these Terms</li>
                    <li>Late payments may result in service suspension</li>
                    <li>You authorize us to charge your payment method for all fees</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Taxes</h3>
                  <p className="text-slate-600">
                    You're responsible for all applicable taxes, duties, and assessments except for taxes on our net income.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Acceptable Use</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Permitted Use</h3>
                  <p className="text-slate-600 mb-2">You may use our Service to:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>Manage legitimate business communications</li>
                    <li>Schedule appointments and handle customer inquiries</li>
                    <li>Capture leads and qualify prospects</li>
                    <li>Handle emergency service requests</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Prohibited Use</h3>
                  <p className="text-slate-600 mb-2">You may NOT use our Service for:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>Illegal activities or violation of laws or regulations</li>
                    <li>Harassment, abuse, or harmful content</li>
                    <li>Spam, unsolicited communications, or telemarketing</li>
                    <li>Adult content, gambling, or other restricted industries</li>
                    <li>Competitive intelligence gathering against us</li>
                    <li>Attempting to reverse engineer or hack our systems</li>
                    <li>Violating intellectual property rights</li>
                    <li>Fraudulent or deceptive practices</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Data and Privacy</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Your Data</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>You retain ownership of all data you provide to our Service</li>
                    <li>You grant us a license to use your data to provide the Service</li>
                    <li>We'll protect your data in accordance with our Privacy Policy</li>
                    <li>You're responsible for the accuracy and legality of data you provide</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Call Recordings</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>All calls are recorded for quality assurance and AI training</li>
                    <li>Recordings are stored securely and used only for legitimate business purposes</li>
                    <li>You consent to call recording by using our Service</li>
                    <li>Your customers will be notified that calls may be recorded</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Compliance</h3>
                  <p className="text-slate-600 mb-2">You're responsible for ensuring your use of our Service complies with applicable laws, including but not limited to:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>Telecommunications regulations</li>
                    <li>Privacy laws (CCPA, GDPR, etc.)</li>
                    <li>Industry-specific regulations</li>
                    <li>Recording consent requirements in your jurisdiction</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Service Level and Availability</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Service Level Commitment</h3>
                  <p className="text-slate-600">
                    We strive to maintain 99.9% uptime for our AI receptionist service. However, we don't guarantee uninterrupted service and aren't liable for service interruptions.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Maintenance</h3>
                  <p className="text-slate-600">
                    We may perform scheduled maintenance that temporarily affects service availability. We'll provide reasonable advance notice when possible.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Emergency Situations</h3>
                  <p className="text-slate-600">
                    While our AI is designed to detect emergencies, you remain responsible for ensuring appropriate emergency response procedures are in place.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-6">Limitation of Liability</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Service Limitations</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>Our AI receptionist is designed to handle common business scenarios but may not be suitable for all situations</li>
                    <li>Complex or highly specialized inquiries may require human intervention</li>
                    <li>We don't guarantee the accuracy of all AI responses</li>
                    <li>Emergency detection is best-effort and shouldn't be relied upon as the sole emergency response system</li>
                  </ul>
                </div>

                <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Liability Limits</h3>
                  <p className="text-slate-600 mb-2 font-medium">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>OUR TOTAL LIABILITY IS LIMITED TO THE AMOUNT YOU PAID IN THE 12 MONTHS PRECEDING THE CLAIM</li>
                    <li>WE'RE NOT LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                    <li>WE'RE NOT LIABLE FOR LOST PROFITS, LOST DATA, OR BUSINESS INTERRUPTION</li>
                    <li>SOME JURISDICTIONS DON'T ALLOW THESE LIMITATIONS, SO THEY MAY NOT APPLY TO YOU</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Termination</h2>
                
                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Termination by You</h3>
                  <p className="text-slate-600">
                    You may cancel your subscription at any time through your account dashboard. Cancellation takes effect at the end of your current billing period.
                  </p>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Termination by Us</h3>
                  <p className="text-slate-600 mb-2">We may terminate your account immediately if you:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>Violate these Terms or our Acceptable Use Policy</li>
                    <li>Fail to pay fees when due</li>
                    <li>Engage in fraudulent or harmful activities</li>
                    <li>Use the Service in ways that harm our business or other users</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="font-display text-xl font-normal text-slate-800 mb-3">Effect of Termination</h3>
                  <p className="text-slate-600 mb-2">Upon termination:</p>
                  <ul className="space-y-2 text-slate-600">
                    <li>Your access to the Service will cease immediately</li>
                    <li>We'll stop answering calls for your business</li>
                    <li>Your data will be deleted according to our Privacy Policy</li>
                    <li>All outstanding fees become immediately due</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="font-display text-2xl font-normal text-slate-800 mb-4">Contact Information</h2>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6">
                  <h3 className="font-display text-lg font-normal text-slate-800 mb-3">Questions about these Terms?</h3>
                  <div className="space-y-2 text-slate-600">
                    <p><strong>Email:</strong> legal@nodayoff.ai</p>
                    <p><strong>Phone:</strong> (310) 861-3287</p>
                    <p><strong>Business Information:</strong> receptioneer.ai</p>
                  </div>
                </div>
              </section>

              <div className="text-center pt-8 border-t border-slate-200">
                <p className="text-slate-500 text-sm mb-4">
                  These Terms of Service were last updated on January 1, 2025. Previous versions are available upon request.
                </p>
                <p className="text-slate-600 font-medium">
                  By using receptioneer.ai, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-12 px-6 bg-white/50">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex flex-col items-center md:items-start gap-3">
            <div className="font-display text-xl text-gradient-purple">
            receptioneer.ai
            </div>
            <p className="text-slate-500 text-center md:text-left text-sm">
              AI receptionists for service businesses
            </p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <div className="flex gap-6">
              <a href="/" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Home</a>
              <a href="/terms" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Terms</a>
              <a href="/privacy" className="text-slate-500 hover:text-indigo-600 transition-colors text-sm">Privacy</a>
            </div>
            <div className="text-slate-400 text-xs">
              © 2025 nodayoff.ai. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;